import { Chip, Icon, Tooltip } from '@elipssolution/harfang';
import { alpha, Stack, styled, useTheme } from '@mui/material';
import { useMemo } from 'react';

import useFormat from '../../../hooks/useFormat';
import {
	DocumentUploadStatusEnum,
	toDocumentUploadStatusIconEnum,
	toDocumentUploadStatusLabelEnum,
} from '../types/documentUpload';

const StyledStatusIndicator = styled(Stack)(({ theme: { spacing } }) => ({
	borderRadius: '50%',
	padding: spacing(0.5),
}));

const StatusIndicator = ({ status }: { status: DocumentUploadStatusEnum }) => {
	const { palette } = useTheme();
	const { isMobileFormat } = useFormat();

	const chipStatusMapper = useMemo(
		() =>
			new Map([
				[
					DocumentUploadStatusEnum.FAILED,
					{
						color: palette.error.main,
						backgroundColor: alpha(palette.error.main, 0.2),
						label: toDocumentUploadStatusLabelEnum(DocumentUploadStatusEnum.FAILED),
						iconPath: toDocumentUploadStatusIconEnum(DocumentUploadStatusEnum.FAILED) || '',
						tooltipContent:
							'Une erreur est survenue lors du dépôt. Une nouvelle tentative sera effectuée lors de la synchronisation quotidienne.',
					},
				],
				[
					DocumentUploadStatusEnum.WAITING,
					{
						color: '#9c27b0',
						backgroundColor: alpha('#9c27b0', 0.2),
						label: toDocumentUploadStatusLabelEnum(DocumentUploadStatusEnum.WAITING),
						iconPath: toDocumentUploadStatusIconEnum(DocumentUploadStatusEnum.WAITING) || '',
					},
				],
				[
					DocumentUploadStatusEnum.SENT,
					{
						color: palette.info.main,
						backgroundColor: alpha(palette.info.main, 0.2),
						label: toDocumentUploadStatusLabelEnum(DocumentUploadStatusEnum.SENT),
						iconPath: toDocumentUploadStatusIconEnum(DocumentUploadStatusEnum.SENT) || '',
					},
				],
				[
					DocumentUploadStatusEnum.PROCESSED,
					{
						color: palette.success.main,
						backgroundColor: alpha(palette.success.main, 0.2),
						label: toDocumentUploadStatusLabelEnum(DocumentUploadStatusEnum.PROCESSED),
						iconPath: toDocumentUploadStatusIconEnum(DocumentUploadStatusEnum.PROCESSED) || '',
					},
				],
				[
					DocumentUploadStatusEnum.DUPLICATED,
					{
						color: palette.warning.main,
						backgroundColor: alpha(palette.warning.main, 0.2),
						label: toDocumentUploadStatusLabelEnum(DocumentUploadStatusEnum.DUPLICATED),
						iconPath: toDocumentUploadStatusIconEnum(DocumentUploadStatusEnum.DUPLICATED) || '',
					},
				],
			]),
		[palette],
	);

	const { color, backgroundColor, label, iconPath, tooltipContent } = chipStatusMapper.get(status) || {
		content: '',
		color: 'info',
		iconPath: '',
	};

	return isMobileFormat ? (
		<StyledStatusIndicator color={color} sx={{ backgroundColor }}>
			<Icon path={iconPath} />
		</StyledStatusIndicator>
	) : (
		<Tooltip content={tooltipContent}>
			<Chip color={color} label={label} />
		</Tooltip>
	);
};

export default StatusIndicator;
